<script setup lang="ts">
import { useQueries } from "@tanstack/vue-query";
import EditionHitsGrid from "@/components/editions/EditionHitsGrid.vue";
import ArticleCard from "@/components/ArticleCard.vue";
import type { EditionHit } from "@/types/edition";
import {
  getTitleEditionHitsSearch,
  getTitleArticlesSearch,
  EditionHitsInclude,
  ArticleInclude,
} from "@/repository";
import { toRef, computed } from "vue";
import LoadingIndicator from "@/components/general/LoadingIndicator.vue";
import { EmptyState, ErrorState } from "@/components/states";

const props = defineProps<{
  searchQuery: string;
}>();

defineEmits(["openTab"]);

const q = toRef(props, "searchQuery");

const results = useQueries({
  queries: [
    {
      queryKey: ["article-search", q],
      queryFn: () =>
        getTitleArticlesSearch({
          query: q.value,
          cursor: "null",
          include: [ArticleInclude.EditionTitle],
        }),
      enabled: computed(() => q.value.length > 1),
    },
    {
      queryKey: ["edition-hits-search", q],
      queryFn: () =>
        getTitleEditionHitsSearch({
          query: q.value,
          cursor: "null",
          include: [
            EditionHitsInclude.EndUserEditionData,
            EditionHitsInclude.EndUserSubscription,
          ],
        }),
      enabled: computed(() => q.value.length > 1),
    }
  ],
});

const articles = computed(() => results.value[0]);
const articlesIsEmpty = computed(() => {
  // Only show empty state if query was enabled and executed successfully
  const isEnabled = q.value.length > 1;
  const articleData = articles.value?.data?.data.data;
  return isEnabled && articles.value?.isSuccess && !articleData?.length;
});

const hitsQuery = computed(() => results.value[1]);
const hitsData = computed<EditionHit[] | undefined>(
  () => hitsQuery.value?.data?.data?.data
);
const hitsList = computed<EditionHit[]>(() => hitsData.value ?? []);

const hitsNext = computed(
  () => hitsQuery.value?.data?.data?.meta?.next_cursor ?? null
);

const hitsIsEmpty = computed(() => {
  // Only show empty state if query was enabled and executed successfully
  const isEnabled = q.value.length > 1;
  return isEnabled && hitsQuery.value?.isSuccess && hitsList.value.length === 0;
});
</script>

<template>
  <div class="py-4">
    <div>
      <h2 class="font-bold text-lg px-4">{{ $t("search.label.magazines") }}</h2>
      <div class="mt-2">
        <div
          v-if="hitsQuery.isLoading || hitsQuery.isError || hitsIsEmpty"
          class="flex-center min-h-40"
        >
          <LoadingIndicator v-if="hitsQuery.isLoading" class="text-icon-brand" />
          <ErrorState
            v-else-if="hitsQuery.isError"
            :contentName="$t('common.editions')"
            @refetch="results[1].refetch"
          />
          <EmptyState
            v-else-if="hitsIsEmpty"
            :title="$t('search.no_results.title')"
            :description="$t('search.no_results.description')"
          />
        </div>
        <template v-else>
          <div class="px-4">
            <EditionHitsGrid 
              :hits="hitsList.slice(0, 4)" 
              :query="q"
            />
          </div>

          <div class="py-2 px-4" v-if="hitsNext">
            <button
              class="text-text-brand underline"
              @click="$emit('openTab', 'magazines')"
            >
              {{ $t("search.view_more") }}
            </button>
          </div>
        </template>
      </div>
    </div>
    <div class="mt-4 px-4">
      <h2 class="font-bold text-lg">{{ $t("search.label.articles") }}</h2>
      <div class="mt-2">
        <div
          v-if="articles.isLoading || articles.isError || articlesIsEmpty"
          class="flex-center min-h-40"
        >
          <LoadingIndicator v-if="articles.isLoading" class="text-icon-brand" />
          <ErrorState
            v-else-if="articles.isError"
            :contentName="$t('common.articles')"
            @refetch="results[0].refetch"
          />
          <EmptyState
            v-else-if="articlesIsEmpty"
            :title="$t('search.no_results.title')"
            :description="$t('search.no_results.description')"
          />
        </div>
        <template v-else>
          <div class="py-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ArticleCard
              class="h-20"
              v-for="article in articles.data?.data.data"
              :key="article.id"
              :article="article"
              vertical
            />
          </div>
          <div class="py-2" v-if="articles.data?.data.meta.next_cursor">
            <button
              class="text-text-brand underline"
              @click="$emit('openTab', 'articles')"
            >
              {{ $t("search.view_more") }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
